import { useEffect, useState, type ReactNode } from "react"
import { Activity, ArrowUpRight, Boxes, ChevronRight, Command, Globe2, LayoutDashboard, Menu, Moon, Radio, Search, Ship, Sparkles, Sun, TriangleAlert, TrendingUp, X } from "lucide-react"
import { useApp, useToasts } from "./store"

const navigation = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "world", label: "World view", icon: Globe2 },
  { id: "intel", label: "Disruptions", icon: TriangleAlert },
  { id: "market", label: "Markets", icon: TrendingUp },
  { id: "fleet", label: "Fleet & ports", icon: Ship },
  { id: "warehouse", label: "Warehouse", icon: Boxes },
  { id: "chat", label: "AI assistant", icon: Sparkles },
] as const

export function ThemeToggle() {
  const [theme, setTheme] = useState(() => document.documentElement.dataset.theme === "light" ? "light" : "dark")
  useEffect(() => {
    document.documentElement.dataset.theme = theme
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme === "dark" ? "#0b111a" : "#f5f7fa")
    try { localStorage.setItem("supplylens-theme", theme) } catch { /* Browsing with storage disabled still supports the toggle. */ }
  }, [theme])
  return (
    <div className="theme-switch" role="group" aria-label="Appearance">
      <button type="button" aria-label="Light theme" aria-pressed={theme === "light"} onClick={() => setTheme("light")} title="Light theme"><Sun size={16} /><span>Light</span></button>
      <button type="button" aria-label="Dark theme" aria-pressed={theme === "dark"} onClick={() => setTheme("dark")} title="Dark theme"><Moon size={15} /><span>Dark</span></button>
    </div>
  )
}

export function WorkspaceChrome({ children, status, tools }: { children: ReactNode; status: ReactNode; tools: ReactNode }) {
  const tab = useApp((s) => s.tab)
  const setTab = useApp((s) => s.setTab)
  const openPalette = useApp((s) => s.setPaletteOpen)
  const connection = useApp((s) => s.connection)
  const [active, setActive] = useState("overview")
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => { if (tab === "globe") setActive("world"); else setActive((id) => id === "world" ? "overview" : id) }, [tab])
  useEffect(() => {
    const close = (e: KeyboardEvent) => { if (e.key === "Escape") setMobileOpen(false) }
    document.addEventListener("keydown", close)
    return () => document.removeEventListener("keydown", close)
  }, [])

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 901px)")
    const closeOnDesktop = () => { if (desktop.matches) setMobileOpen(false) }
    desktop.addEventListener("change", closeOnDesktop)
    return () => desktop.removeEventListener("change", closeOnDesktop)
  }, [])

  useEffect(() => {
    if (!mobileOpen) return
    const main = document.querySelector<HTMLElement>(".workspace-main")
    const sidebar = document.querySelector<HTMLElement>(".workspace-sidebar")
    const buttons = sidebar?.querySelectorAll<HTMLButtonElement>("button")
    if (main) main.inert = true
    buttons?.[0]?.focus()
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !buttons?.length) return
      const first = buttons[0]!, last = buttons[buttons.length - 1]!
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener("keydown", trapFocus)
    return () => {
      if (main) main.inert = false
      document.removeEventListener("keydown", trapFocus)
      document.querySelector<HTMLButtonElement>(".mobile-menu")?.focus()
    }
  }, [mobileOpen])

  const navigate = (id: string) => {
    setActive(id)
    setMobileOpen(false)
    setTab(id === "world" ? "globe" : "cockpit")
    if (id === "world") return
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const scroller = document.querySelector(".workspace-scroll")
      if (id === "overview") { scroller?.scrollTo({ top: 0, behavior: "smooth" }); return }
      const panel = document.querySelector<HTMLElement>(`[data-panel-id="${id}"]`)
      if (panel) panel.scrollIntoView({ block: "start", behavior: "smooth" })
      else useToasts.getState().pushToast("This panel is not in your saved layout. Choose Reset layout to restore all panels.", "info")
    }))
  }

  return (
    <div className="workspace">
      <a className="skip-link" href="#workspace-main">Skip to dashboard</a>
      {mobileOpen && <button className="sidebar-backdrop" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />}
      <aside className={`workspace-sidebar ${mobileOpen ? "is-open" : ""}`} aria-label="Main navigation">
        <button className="brand" onClick={() => navigate("overview")} aria-label="SupplyLens overview">
          <span className="brand-mark"><Boxes size={22} strokeWidth={1.6} /></span>
          <span>Supply<span className="brand-accent">Lens</span><small>LOGISTICS INTELLIGENCE</small></span>
        </button>
        <div className="workspace-label">Workspace <span>GLOBAL</span></div>
        <nav aria-label="Workspace sections">
          {navigation.map(({ id, label, icon: Icon }) => (
            <button key={id} className={`sidebar-link ${active === id ? "is-active" : ""}`} aria-current={active === id ? "location" : undefined} onClick={() => navigate(id)}>
              <Icon size={18} strokeWidth={1.7} /><span>{label}</span>{active === id && <span className="nav-dot" />}
            </button>
          ))}
        </nav>
        <div className="sidebar-assistant">
          <span className="assistant-symbol"><Sparkles size={19} /></span>
          <strong>A clearer next move.</strong>
          <p>Explore the signals behind your supply chain.</p>
          <button onClick={() => navigate("chat")}>Ask the assistant <ArrowUpRight size={15} /></button>
        </div>
        <div className="sidebar-bottom">
          <div className="connection-line"><Radio size={15} /><span>{connection === "connected" ? "Realtime connected" : connection === "connecting" ? "Connecting to stream" : "Realtime reconnecting"}</span><i data-connected={connection === "connected"} /></div>
          <div className="workspace-account"><span className="workspace-avatar"><Globe2 size={19} /></span><div><strong>Global workspace</strong><small>Supply chain operations</small></div></div>
        </div>
      </aside>
      <div className="workspace-main" id="workspace-main" tabIndex={-1}>
        <header className="workspace-topbar">
          <button className="icon-button mobile-menu" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? "Close navigation" : "Open navigation"} aria-expanded={mobileOpen}>{mobileOpen ? <X size={20} /> : <Menu size={20} />}</button>
          <div className="breadcrumb"><span>Workspace</span><ChevronRight size={13} /><strong>{tab === "globe" ? "World view" : "Overview"}</strong></div>
          <button className="workspace-search" onClick={() => openPalette(true)} aria-label="Open command palette"><Search size={16} /><span>Search regions, views, commands…</span><kbd><Command size={10} /> K</kbd></button>
          <ThemeToggle />
          <span className="topbar-avatar" title="Global workspace"><Globe2 size={17} /></span>
        </header>
        <div className="workspace-context"><span><Activity size={13} /> Global operations <span className="context-divider">/</span> {tab === "globe" ? "World view" : "Command center"}</span><div className="workspace-status">{status}</div></div>
        <main className="workspace-body">{children}</main>
        <footer className="workspace-footer"><span>SupplyLens <span className="footer-separator">·</span> A wider view. A clearer decision.</span><div>{tools}</div></footer>
      </div>
    </div>
  )
}
